import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { useRootStore } from "../../../stores/hooks";
import { AutomationCards } from "../AutomationCards";
import { observer } from "mobx-react-lite";
import { Footer, StyledTabs } from "../style";
import { t } from "i18next";
import { LimitsDisclamer } from "./LimitsDisclamer";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  Button,
  buttonDefaultStyles,
  Scrollbar,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import type { ApplicationGroup } from "./utils";
import { getGroupedAutomations, getOrderedAutomations } from "./utils";

const addButtonStyles = {
  ...buttonDefaultStyles,
  height: 34,
};

export const AutomationListContent = observer<{
  onClose?: () => void;
}>(({ onClose }) => {
  const store = useRootStore();
  const { automations, applications } = store;

  const navigate = useNavigate();
  const [groupedAutomations, setGroupedAutomations] = useState<
    ApplicationGroup[]
  >([]);

  const addAutomation = (): void => {
    navigate("/create");
  };

  const getAddButton = (disabled?: boolean): ReactElement => (
    <Button
      iconName="plus"
      iconSize="S"
      styles={addButtonStyles}
      className="footer-action-button"
      disabled={disabled}
      onClick={addAutomation}
    >
      {t("general.add-automation")}
    </Button>
  );

  useEffect(() => {
    const mapAutomations = getGroupedAutomations(
      automations.list,
      applications.list,
    );
    const arrayAutomations: ApplicationGroup[] = Array.from(
      mapAutomations,
      ([key, value]) => {
        return { appName: key, automations: value };
      },
    );

    const orderedAutomations = getOrderedAutomations(
      applications.list,
      arrayAutomations,
    ).filter((a) => a.automations.length > 0);
    setGroupedAutomations(orderedAutomations);
  }, [automations.list, applications.list]);

  const debouncedScroll = debounce((topState: number) => {
    automations.setScrollListState(topState);
  }, 500);

  return (
    <>
      <Scrollbar
        gutterTop={5}
        gutterRight={5}
        gutterBottom={5}
        noScrollX
        style={{ flexGrow: 1 }}
        scrollTop={automations.scrollListState}
        onScroll={(event) => {
          const top = (event as unknown as HTMLElement).scrollTop;
          debouncedScroll(top);
        }}
      >
        <StyledTabs />

        <LimitsDisclamer />

        {groupedAutomations.map((item) => (
          <AutomationCards
            key={item.appName}
            appName={item.appName}
            automations={item.automations}
            onClose={onClose}
          />
        ))}
      </Scrollbar>

      <Footer>
        {automations.isLimitReached ? (
          <Tooltip
            display="block"
            text={t("automation-list.reached-max-automations", {
              number: store.featureFlags.limits.maxAutomationsPerSolution,
            })}
            target={getAddButton(true)}
          />
        ) : (
          getAddButton()
        )}
      </Footer>
    </>
  );
});
